import { Modal } from '@evisort/eds/components/Modal/Modal';
import { useToast } from '@evisort/eds/components/Toast/hooks';
import { noop } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Text } from '~/eds/src/components/Text/Text';
import { slices } from '~/redux/slices';
import { useAutomationHubPath } from '~/routing/automationHubPaths';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';

/**
 * A modal to redirect from other domains to creating Field AI models
 * @returns
 */
export const RedirectToXRayModal = () => {
  const { navigate } = useRouting();
  const automationHubFieldsPath = useAutomationHubPath(
    RoutePathType.AutomationHubFields,
  );

  const dispatch = useDispatch();

  const redirect = useSelector(slices.xRay.selectors.selectRedirect);

  const { toast } = useToast();

  const intl = useIntl();

  const clearRedirect = () => dispatch(slices.xRay.actions.setRedirect(null));

  const primaryAction = {
    text: intl.formatMessage({
      id: 'RedirectToXRayModal.confirmButton',
      defaultMessage: 'Confirm',
      description: 'Confirm button text',
    }),
    onClick: () => {
      if (redirect) {
        dispatch(slices.xRay.actions.resetXRay());
        dispatch(slices.xRay.actions.setInstructions(redirect.instructions));
        navigate(`${automationHubFieldsPath}/new`);
        clearRedirect();
        toast({
          status: 'informational',
          title: intl.formatMessage({
            id: 'RedirectToXRayModal.toastTitle',
            defaultMessage:
              'Instructions for the model have been auto-populated',
            description: 'Toast title when instructions are auto-populated',
          }),
          text: intl.formatMessage({
            id: 'RedirectToXRayModal.toastMessage',
            defaultMessage: 'You can edit the instructions.',
            description: 'Toast message about editing instructions',
          }),
          onDismiss: noop,
        });
      }
    },
  };

  const cancelText = intl.formatMessage({
    id: 'RedirectToXRayModal.cancelButton',
    defaultMessage: 'Cancel',
    description: 'Cancel button text',
  });

  return (
    <Modal
      isVisible={redirect !== null}
      onHide={clearRedirect}
      header={{
        title: intl.formatMessage({
          id: 'RedirectToXRayModal.title',
          defaultMessage: 'Use This Question for a New Field Model',
          description:
            'Modal title for redirecting to new field model creation',
        }),
      }}
      footer={{
        actions: [
          {
            label: cancelText,
            text: cancelText,
            variant: 'tertiary',
            onClick: clearRedirect,
          },
          {
            ...primaryAction,
            label: primaryAction.text,
            variant: 'primary',
          },
        ],
      }}
    >
      <Text>
        <p>
          <FormattedMessage
            id="RedirectToXRayModal.confirmationQuestion"
            defaultMessage="Would you like to create a New Field Model based on the question selected?"
            description="Question asking user to confirm model creation"
          />
        </p>
        <p>
          <FormattedMessage
            id="RedirectToXRayModal.redirectDescription"
            defaultMessage="This action will take you to the place to create a New Field Model powered by AI based on the question you selected."
            description="Description of what happens when user confirms"
          />
        </p>
      </Text>
    </Modal>
  );
};
